/**
 * AKOA (Automatic Knife Owner Agreement) session state.
 *
 * Signing is remembered for the session in `sessionStorage` and mirrored onto the
 * cart as attributes, so checkout and fulfilment can see the signature. The keys
 * are read across the theme (legacy product form, Threekit customizer, Lifesharp
 * flow), which is why they keep their original names.
 *
 * Pure module: no Lit, no custom elements, no globals installed. Importable from
 * the Preact bundle for render-time state — `web_components/entry/akoa.ts` is
 * what publishes the `window.akoa` API.
 */
export const AKOA_SIGNED_EVENT = 'akoa::signed';
const CART_UPDATE_ATTRIBUTES_EVENT = 'cart:updateAttributes';
const STORAGE_KEYS = {
    accepted: 'accepted_akoa',
    name: 'accepted_akoa_name',
    dateIso: 'accepted_akoa_date_iso',
    date: 'accepted_akoa_date',
};
function read(key) {
    try {
        return sessionStorage.getItem(key);
    }
    catch {
        // Private-mode Safari and storage-blocked embeds throw on access.
        return null;
    }
}
/**
 * True once the shopper has signed this session.
 *
 * Name *and* date, not the `accepted_akoa` flag alone: line-item properties and
 * the cart attributes both need the pair, so a half-written record must not read
 * as signed.
 */
export function isAkoaCompleted() {
    return !!read(STORAGE_KEYS.name) && !!read(STORAGE_KEYS.date);
}
export function getAkoaSignature() {
    const name = read(STORAGE_KEYS.name);
    const date = read(STORAGE_KEYS.date);
    if (!name || !date)
        return null;
    return { name, date, dateIso: read(STORAGE_KEYS.dateIso) ?? '' };
}
function formatAkoaDate(date) {
    return new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    }).format(date);
}
/**
 * Records a signature, mirrors it onto the cart and announces `akoa::signed`.
 */
export function signAkoa(name) {
    const now = new Date();
    const signature = {
        name,
        dateIso: now.toISOString(),
        date: formatAkoaDate(now),
    };
    try {
        sessionStorage.setItem(STORAGE_KEYS.accepted, 'true');
        sessionStorage.setItem(STORAGE_KEYS.name, signature.name);
        sessionStorage.setItem(STORAGE_KEYS.dateIso, signature.dateIso);
        sessionStorage.setItem(STORAGE_KEYS.date, signature.date);
    }
    catch {
        // Without storage the agreement still reaches the cart; only the "already
        // signed" memory is lost, so the shopper is asked again.
    }
    document.dispatchEvent(new CustomEvent(CART_UPDATE_ATTRIBUTES_EVENT, {
        detail: {
            payload: [
                { key: STORAGE_KEYS.accepted, value: 'true' },
                { key: STORAGE_KEYS.name, value: signature.name },
                { key: STORAGE_KEYS.dateIso, value: signature.dateIso },
            ],
        },
    }));
    document.dispatchEvent(new CustomEvent(AKOA_SIGNED_EVENT, {
        detail: signature,
        bubbles: true,
        composed: true,
    }));
    return signature;
}
/** Forgets the signature — used by flows that swap in a non-automatic knife. */
export function clearAkoa() {
    try {
        Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
    }
    catch {
        // Nothing to clear when storage is unavailable.
    }
}
